import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Pencil } from 'lucide-react';
import { memo, useState } from 'react';
import z from 'zod';

import { IconButton } from '@doger/ui/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@doger/ui/components/ui/dialog';
import {
  RadioGroup,
  RadioGroupItem,
} from '@doger/ui/components/ui/radio-group';
import { $$toast } from '@doger/ui/components/ui/toast';
import { useAppForm } from '@doger/ui/form';

import type { FindOneUserResponse } from '@doger/admin/shared/api';
import { UserRole } from '@doger/admin/shared/api';
import { $$privateApi } from '@doger/admin/shared/api';

import styles from './role-changer.module.scss';

type RoleChangerProps = {
  user: FindOneUserResponse;
};

const schema = z.object({
  role: z.enum([UserRole.ADMIN, UserRole.USER]),
});

export const RoleChanger = memo<RoleChangerProps>(({ user }) => {
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);

  const changeRoleMutation = useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: UserRole }) =>
      $$privateApi.toggleUserRole({ userId, role }),

    onSuccess: (_, variables) => {
      queryClient.setQueryData(
        ['user', user.id],
        (old: FindOneUserResponse) => {
          if (!old) return old;

          return {
            ...old,
            role: variables.role,
          };
        }
      );

      queryClient.setQueryData(
        ['users'],
        (old: { pageParams: number[]; pages: FindOneUserResponse[] }) => {
          if (!Array.isArray(old?.pages)) return old;

          return {
            ...old,
            pages: old.pages.map((user) =>
              user.id === variables.userId
                ? { ...user, role: variables.role }
                : user
            ),
          };
        }
      );

      queryClient.invalidateQueries({ queryKey: ['user', user.id] });
      queryClient.invalidateQueries({ queryKey: ['users'] });

      setIsOpen(false);

      $$toast.success(
        `User ${user.email} role has been changed to ${variables.role}.`
      );
    },
    onError: (error, variables) => {
      console.error(error);

      $$toast.error(
        `An error occurred while changing the user role to ${variables.role}. Please try again.`
      );
    },
  });

  const form = useAppForm({
    defaultValues: {
      role: user.role,
    },
    validators: {
      onChange: schema,
      onSubmit: schema,
    },
    onSubmit: async ({ value }) => {
      await changeRoleMutation.mutateAsync({
        role: value.role,
        userId: user.id,
      });
    },
  });

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <IconButton
          size='xs'
          variant='text'
          disabled={changeRoleMutation.isPending}
          className={styles.toggleWhitelistButton}
        >
          <Pencil width={16} height={16} />
        </IconButton>
      </DialogTrigger>

      <DialogContent
        style={{
          top: '50%',
          transform: 'translate(-50%, -50%)',
          maxWidth: '460px',
        }}
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();

            form.handleSubmit();
          }}
        >
          <DialogHeader>
            <DialogTitle>Change User Role</DialogTitle>
            <DialogDescription>
              Change the role of the user to {user.role}.
            </DialogDescription>
          </DialogHeader>

          <DialogBody>
            <form.Field
              name='role'
              children={(field) => (
                <RadioGroup
                  value={field.state.value}
                  onValueChange={(value) =>
                    field.handleChange(value as UserRole)
                  }
                  className={styles.roleSelector}
                >
                  {[UserRole.ADMIN, UserRole.USER].map((role) => (
                    <label key={role} className={styles.roleOption}>
                      <RadioGroupItem
                        value={role}
                        className={styles.roleItem}
                      />

                      <span className={styles.roleLabel}>{role}</span>
                    </label>
                  ))}
                </RadioGroup>
              )}
            />
          </DialogBody>

          <DialogFooter className={styles.dialogFooter}>
            <form.AppForm>
              <form.ResetButton
                variant='outlined'
                onClick={() => {
                  setIsOpen(false);
                }}
                size='md'
                palette='ghost'
              >
                {() => <>Cancel</>}
              </form.ResetButton>
            </form.AppForm>

            <form.AppForm>
              <form.SubmitButton
                variant='contained'
                size='md'
                palette='primary'
              >
                {({ isSubmitting }) =>
                  isSubmitting ? 'Processing...' : 'Change Role'
                }
              </form.SubmitButton>
            </form.AppForm>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
});
